import {lazy, Suspense, useCallback, useEffect, useState} from 'react';
import {Routes, Route, Navigate, useNavigate, useLocation} from 'react-router-dom';
import {observer} from 'mobx-react-lite';
import {Layout} from 'antd';
import CompHeader from '@/components/Header';
import CompSpin from '@/components/Spin';
import ServiceInfoStore from '@/store/ServiceInfoStore';
import {siderStyle} from '@/dicts/constants';
import {ServiceStatusEnum} from '@/dicts/enum';

import CompMenu from './menu';
import styles from './index.module.less';

// const Welcome = lazy(() => import('@/modules/Welcome'));
const ConsolePage = lazy(() => import('@/modules/ConsolePage'));
const ApplicationForm = lazy(() => import('@/modules/ApplicationForm'));
const ServiceManagement = lazy(() => import('@/modules/ServiceManagement'));
const AlarmManagement = lazy(() => import('@/modules/AlarmManagement'));
const DataStatistics = lazy(() => import('@/modules/DataStatistics'));
const AlarmList = lazy(() => import('@/modules/AlarmList'));
const LogQuery = lazy(() => import('@/modules/LogQuery'));
const PurchaseCaptchaService = lazy(() => import('@/modules/PurchaseCaptchaService'));


const {Content, Sider} = Layout;

// 需要隐藏Sider的页面路径
const hiddenSiderPaths = [
    '/purchase-captcha',
    '/purchase-ip-threat',
];

const serviceTitle: {[key: string]: string} = {
    '/service-management': '服务管理',
    '/data-statistics': '数据统计',
    '/alarm-management': '告警管理',
    '/alarm-list': '告警管理',
    '/log-query': '日志查询',
    '/application-form': '填写申请单',
    '/purchase-captcha': '购买验证码服务',
    '/purchase-ip-threat': '购买IP风险画像服务',
};

const App = observer(function App() {
    const navigate = useNavigate();
    const location = useLocation();
    const {pathname} = location;
    const {
        getServiceInfo,
        loading,
        serviceStatus,
    } = ServiceInfoStore;

    const [collapsed, setCollapsed] = useState(false);
    const [serviceName, setServiceName] = useState('');
    const [tabsItems, setTabsItems] = useState<any[]>([]);
    const [activeKey, setActiveKey] = useState('');
    const [serviceLoading, setServiceLoading] = useState(true);

    useEffect(
        () => {
            const initializeServiceInfo = async () => {
                try {
                    await getServiceInfo();
                } catch (error) {
                    console.error('初始化服务信息失败:', error);
                }
            };

            initializeServiceInfo();

            // 页面卸载时清理store状态
            return () => {
                ServiceInfoStore.clearStoreData();
            };
        },
        [getServiceInfo]
    );

    useEffect(
        () => {
            // 如果是告警管理，则显示告警管理的tab
            if (['/alarm-management', '/alarm-list'].includes(pathname)) {
                setTabsItems([
                    {
                        key: '/alarm-management',
                        label: '告警配置',
                    },
                    {
                        key: '/alarm-list',
                        label: '告警记录',
                    },
                ]);
                setActiveKey(pathname);
            } else {
                setTabsItems([]);
            }
        },
        [pathname]
    );
    // 服务名称
    useEffect(
        () => {
            if (serviceStatus === null) {
                return;
            }
            const serviceName = [
                ServiceStatusEnum.NOT_OPENED,
            ].includes(serviceStatus) && pathname === '/service-management'
                ? '开通服务' : serviceTitle[pathname] || '';
            setServiceName(serviceName);
        },
        [serviceStatus, pathname]
    );
    useEffect(
        () => {
            setServiceLoading(loading);
        },
        [loading]
    );

    const handleTabsChange = useCallback(
        (key: string) => {
            navigate(key);
        },
        [navigate]
    );

    const routers = (
        <Suspense fallback={null}>
            <Routes>
                {/* <Route path="welcome" element={<Welcome />} /> */}
                <Route path="console" element={<ConsolePage />} />
                <Route path="application-form" element={<ApplicationForm />} />
                <Route path="service-management" element={<ServiceManagement />} />
                <Route path="alarm-management" element={<AlarmManagement />} />
                <Route path="data-statistics" element={<DataStatistics />} />
                <Route path="alarm-list" element={<AlarmList />} />
                <Route path="log-query" element={<LogQuery />} />
                <Route path="purchase-captcha" element={<PurchaseCaptchaService />} />
                <Route path="purchase-ip-threat" element={<PurchaseCaptchaService />} />
                <Route path="*" element={<Navigate replace to="/service-management" />} />
            </Routes>
        </Suspense>
    );

    // 未开通
    // if (!isTrial || isExpired) {
    //     return <ApplicationForm />;
    // }
    if (serviceLoading) {
        return (
            <CompSpin spinning={loading}>
                <Layout style={{height: 'calc(100vh - 50px)', width: '100%'}}>
                    <Content />
                </Layout>
            </CompSpin>
        );
    }
    return (
        <Layout style={{height: 'calc(100vh - 50px)'}}>
            {!hiddenSiderPaths.includes(pathname) && (
                <Sider
                    width={180}
                    style={siderStyle}
                    collapsedWidth={0}
                    trigger={null}
                    collapsible
                    collapsed={collapsed}
                    className={`${collapsed && styles.menuGlobalCollapsed}`}
                >
                    <div className={styles.menuGlobalSiderbarHead}>
                        <span className={styles.menuGlobalSiderbarHeadText}>
                            {collapsed ? '验' : '验证码 Captcha'}
                        </span>
                    </div>
                    <CompMenu />
                    <div
                        className={styles.menuRightNav}
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        <span className={styles.collapseIcon}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1em" fill="currentColor">
                                <path d="M672 512l-320 256V256z"></path>
                            </svg>
                        </span>
                    </div>
                </Sider>
            )}
            <Layout style={{overflow: 'auto'}}>
                <div className={styles.contentGlobalWrap}>
                    <CompHeader
                        serviceName={serviceName}
                        tabsItems={tabsItems}
                        activeKey={activeKey}
                        onTabsChange={handleTabsChange}
                    />
                    <Content
                        style={{
                            padding: 16,
                            overflow: 'auto',
                        }}
                    >
                        {routers}
                    </Content>
                </div>
            </Layout>
        </Layout>
    );
});
export default App;
